/**
 * The nearest wall facing a wall across the room (F4). Its own module so the
 * shared-wall movers (`sharedDrag`, `heldMove`) and `measure` can all use it
 * without an import cycle.
 */
import { POLICY } from './policy';
import { area, axisOf, edgeOf, outN } from './geom';
import type { Axis, Corner, Room } from './types';

/** The nearest wall facing `vid` across the room's interior. */
export function findOpposite(r: Room, vid: number): { a: Corner; b: Corner; d: number } | null {
  const E = edgeOf(r, vid);
  if (!E) return null;
  const t = axisOf(E.a, E.b);
  if (t !== 'h' && t !== 'v') return null;
  const A = area(r), n = outN(r, E.a, E.b, A);
  const ax: Axis = t === 'h' ? 'y' : 'x', ox: Axis = t === 'h' ? 'x' : 'y', nin = -n[ax];
  const lo = Math.min(E.a[ox], E.b[ox]), hi = Math.max(E.a[ox], E.b[ox]);
  let best: { a: Corner; b: Corner; d: number } | null = null;
  r.corners.forEach((a, i) => {
    const b = r.corners[(i + 1) % r.corners.length];
    if (a.id === vid || axisOf(a, b) !== t) return;
    const d = (a[ax] - E.a[ax]) * nin;
    if (d < POLICY.opposite_min_ft) return;
    const l2 = Math.min(a[ox], b[ox]), h2 = Math.max(a[ox], b[ox]);
    if (Math.min(hi, h2) - Math.max(lo, l2) < POLICY.opposite_min_ft) return;
    const nb = outN(r, a, b, A);
    if (nb[ax] * nin <= 0) return;
    if (!best || d < best.d) best = { a, b, d };
  });
  return best;
}
